import type { CsvRow } from "../types";

const TIMESTAMP_HEADERS = ["timestamp", "time", "datetime", "date", "issued"];

export interface ParseCsvOptions {
  timestampColumn?: string;
  valueColumn?: string;
}

export function parseCsvRows(csvText: string, options: ParseCsvOptions = {}): CsvRow[] {
  const lines = csvText.trim().split(/\r?\n/);
  if (lines.length < 2) throw new Error("CSV has no data rows");

  const headers = lines[0].split(",").map((h) => h.trim());
  const headersLower = headers.map((h) => h.toLowerCase());

  const tsIdx = options.timestampColumn
    ? headersLower.indexOf(options.timestampColumn.toLowerCase())
    : headersLower.findIndex((h) => TIMESTAMP_HEADERS.includes(h));

  if (tsIdx === -1) throw new Error("No timestamp column found in CSV");

  let valueIdx: number;
  if (options.valueColumn) {
    valueIdx = headersLower.indexOf(options.valueColumn.toLowerCase());
  } else {
    valueIdx = headers.findIndex((_, i) => {
      if (i === tsIdx) return false;
      const sample = lines[1]?.split(",")[i]?.trim();
      return sample !== undefined && sample !== "" && !isNaN(parseFloat(sample));
    });
  }
  if (valueIdx === -1) throw new Error("No numeric value column found in CSV");

  const rows: CsvRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const cols = line.split(",");
    const ts = new Date(cols[tsIdx]?.trim());
    const val = parseFloat(cols[valueIdx]?.trim());
    if (isNaN(ts.getTime()) || isNaN(val)) continue;

    let extras: Record<string, string | number> | undefined;
    for (let j = 0; j < headers.length; j++) {
      if (j === tsIdx || j === valueIdx) continue;
      const cell = cols[j]?.trim();
      if (cell === undefined || cell === "") continue;
      extras = extras ?? {};
      const num = parseFloat(cell);
      extras[headers[j]] = !isNaN(num) && String(num) === cell ? num : cell;
    }

    rows.push({ timestamp: ts, value: val, extras });
  }

  if (rows.length === 0) throw new Error("No valid data points found in CSV");
  return rows;
}
